import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  BookUser,
  LoaderCircle,
  MessageSquareText,
  Search,
  Ticket,
  X
} from "lucide-react";
import { api } from "../lib/api";
import type { GlobalSearchResults, TicketSummary } from "../lib/types";
import { CompanyBadge } from "./CompanyBadge";

type SearchDestination =
  | { kind: "contact"; contactId: string }
  | { kind: "ticket"; ticketId: string };

interface SearchItem {
  key: string;
  group: "Contatos" | "Tickets" | "Mensagens";
  title: string;
  description: string;
  meta?: string;
  company?: { id: string; name: string } | null;
  icon: "contact" | "ticket" | "message";
  destination: SearchDestination;
}

function ticketStatusLabel(status: TicketSummary["status"]) {
  if (status === "WAITING") return "Aguardando";
  if (status === "OPEN") return "Em atendimento";
  if (status === "PENDING") return "Aguardando cliente";
  return "Resolvido";
}

function messagePreview(body: string, fallback: string) {
  const clean = body.replace(/\s+/g, " ").trim();
  return clean || fallback;
}

export function buildGlobalSearchItems(
  results?: GlobalSearchResults
): SearchItem[] {
  if (!results) return [];
  return [
    ...results.contacts.map((contact) => ({
      key: `contact-${contact.id}`,
      group: "Contatos" as const,
      title: contact.name,
      description: contact.phone,
      company: contact.company,
      icon: "contact" as const,
      destination: { kind: "contact" as const, contactId: contact.id }
    })),
    ...results.tickets.map((ticket) => ({
      key: `ticket-${ticket.id}`,
      group: "Tickets" as const,
      title: `#${ticket.sequence} · ${ticket.contact.name}`,
      description: messagePreview(
        ticket.messages[0]?.body || "",
        ticket.messages[0]?.type || "Sem mensagens"
      ),
      meta: ticketStatusLabel(ticket.status),
      company: ticket.contact.company,
      icon: "ticket" as const,
      destination: { kind: "ticket" as const, ticketId: ticket.id }
    })),
    ...results.messages.map((message) => ({
      key: `message-${message.id}`,
      group: "Mensagens" as const,
      title: `${message.ticket.contact.name} · Ticket #${message.ticket.sequence}`,
      description: messagePreview(message.body, message.type),
      meta:
        message.direction === "INBOUND"
          ? "Cliente"
          : message.senderUser?.name
            ? `Equipe · ${message.senderUser.name}`
            : "Equipe",
      company: message.ticket.contact.company,
      icon: "message" as const,
      destination: {
        kind: "ticket" as const,
        ticketId: message.ticket.id
      }
    }))
  ];
}

export function GlobalSearch({
  compact = false,
  registerShortcut = true
}: {
  compact?: boolean;
  registerShortcut?: boolean;
}) {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedQuery(query.trim()),
      240
    );
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [debouncedQuery]);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!registerShortcut) return;
    function handleShortcut(event: globalThis.KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [registerShortcut]);

  useEffect(() => {
    if (compact && open) window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [compact, open]);

  const results = useQuery({
    queryKey: ["global-search", "tolerant", debouncedQuery],
    queryFn: ({ signal }) =>
      api<GlobalSearchResults>(
        `/v1/search?q=${encodeURIComponent(debouncedQuery)}&mode=tolerant`,
        { signal }
      ),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000
  });

  const items = useMemo<SearchItem[]>(
    () => buildGlobalSearchItems(results.data),
    [results.data]
  );

  function choose(destination: SearchDestination) {
    setOpen(false);
    setQuery("");
    if (destination.kind === "contact") {
      void navigate({
        to: "/contacts",
        search: { contact: destination.contactId }
      });
      return;
    }
    void navigate({
      to: "/inbox",
      search: { ticket: destination.ticketId }
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!items.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? items.length - 1 : current - 1
      );
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      choose(items[activeIndex]!.destination);
    }
  }

  const searchField = (
    <div className="global-search">
      <Search size={18} />
      <input
        ref={inputRef}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        aria-label="Busca global"
        aria-expanded={open && query.trim().length >= 2}
        aria-controls="global-search-results"
        placeholder="Buscar pessoa, empresa, ticket ou mensagem..."
      />
      {query ? (
        <button
          type="button"
          className="global-search-clear"
          onClick={() => {
            setQuery("");
            inputRef.current?.focus();
          }}
          aria-label="Limpar pesquisa"
        >
          <X size={15} />
        </button>
      ) : !compact ? (
        <kbd>Ctrl K</kbd>
      ) : null}
    </div>
  );

  const resultPanel =
    open && query.trim().length >= 2 ? (
      <div
        className="global-search-results"
        id="global-search-results"
        role="listbox"
        aria-label="Resultados da busca global"
      >
        {results.isLoading || debouncedQuery !== query.trim() ? (
          <div className="global-search-state">
            <LoaderCircle className="spin" size={18} /> Pesquisando...
          </div>
        ) : results.isError ? (
          <div className="global-search-state error">
            Não foi possível pesquisar agora. Tente novamente.
          </div>
        ) : items.length ? (
          <SearchResultGroups
            items={items}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
            onChoose={choose}
          />
        ) : (
          <div className="global-search-state empty">
            <Search size={20} />
            <strong>Nenhum resultado</strong>
            <span>Tente outro nome, telefone, ticket ou trecho de mensagem.</span>
          </div>
        )}
        {items.length ? (
          <footer>Use ↑ ↓ para navegar e Enter para abrir</footer>
        ) : null}
      </div>
    ) : null;

  if (compact) {
    return (
      <div className="global-search-root compact" ref={rootRef}>
        <button
          type="button"
          className="icon-button"
          onClick={() => setOpen(true)}
          aria-label="Abrir busca global"
        >
          <Search size={18} />
        </button>
        {open ? (
          <div className="mobile-global-search-layer" role="presentation">
            <div className="mobile-global-search-card">
              <header>
                {searchField}
                <button
                  type="button"
                  className="global-search-cancel"
                  onClick={() => setOpen(false)}
                >
                  Cancelar
                </button>
              </header>
              {resultPanel}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="global-search-root" ref={rootRef}>
      {searchField}
      {resultPanel}
    </div>
  );
}

function SearchResultGroups({
  items,
  activeIndex,
  onActiveIndexChange,
  onChoose
}: {
  items: SearchItem[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onChoose: (destination: SearchDestination) => void;
}) {
  return (["Contatos", "Tickets", "Mensagens"] as const).map((group) => {
    const groupedItems = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.group === group);
    if (!groupedItems.length) return null;
    return (
      <section className="global-search-group" key={group}>
        <h3>{group}</h3>
        {groupedItems.map(({ item, index }) => (
          <button
            type="button"
            role="option"
            aria-selected={activeIndex === index}
            className={activeIndex === index ? "active" : ""}
            key={item.key}
            onMouseEnter={() => onActiveIndexChange(index)}
            onClick={() => onChoose(item.destination)}
          >
            <span className={`global-result-icon ${item.icon}`}>
              {item.icon === "contact" ? (
                <BookUser size={17} />
              ) : item.icon === "ticket" ? (
                <Ticket size={17} />
              ) : (
                <MessageSquareText size={17} />
              )}
            </span>
            <span className="global-result-copy">
              <span className="global-result-title">
                <strong>{item.title}</strong>
                {item.company ? (
                  <CompanyBadge company={item.company} compact />
                ) : null}
              </span>
              <small>{item.description}</small>
            </span>
            {item.meta ? (
              <span className="global-result-meta">{item.meta}</span>
            ) : null}
          </button>
        ))}
      </section>
    );
  });
}
